/**
 * formacionesMiticasLogic.js — Lógica del minijuego Formaciones Míticas
 *
 * DB necesaria:
 *   tabla: formaciones_miticas
 *     id, nombre, equipo, año, competencia, formacion, fecha_evento, activa, orden, contexto_club
 *
 *   tabla: jugadores (campos extra)
 *     formacion_id (FK -> formaciones_miticas.id)
 *     numero_camiseta (int)
 *     slot (int 0-10, posición en COORDENADAS_FORMACIONES)
 *     contexto = 'formacion_mitica'
 */
import { supabase } from '../../../supabaseClient';

// ── Normalizar texto para comparación ────────────────────────────────────────
export const normalizar = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

// ── Obtener la formación activa del día ───────────────────────────────────────
export const obtenerFormacionActiva = async () => {
  // Hora Argentina UTC-3
  const ahora = new Date();
  const offset = -3 * 60;
  const local = new Date(ahora.getTime() + (offset - ahora.getTimezoneOffset()) * 60000);
  const hoy = local.toISOString().split('T')[0]; // 'YYYY-MM-DD'

  // 1. Buscar si hay una formación con fecha_activacion exacta hoy
  const { data, error } = await supabase
    .from('formaciones_miticas')
    .select('*')
    .eq('activa', true)
    .eq('fecha_activacion', hoy)
    .maybeSingle();

  if (!error && data) return data;

  // 2. Fallback: ciclo infinito
  // Traer todas las formaciones activas con fecha_activacion asignada, ordenadas por orden
  const { data: todas } = await supabase
    .from('formaciones_miticas')
    .select('*')
    .eq('activa', true)
    .not('fecha_activacion', 'is', null)
    .order('orden', { ascending: true });

  if (!todas || todas.length === 0) return null;

  // Calcular días desde la primera fecha del ciclo
  const fechaInicio = new Date(todas[0].fecha_activacion);
  const fechaHoy = new Date(hoy);
  const diasDesde = Math.floor((fechaHoy - fechaInicio) / (1000 * 60 * 60 * 24));
  const idx = ((diasDesde % todas.length) + todas.length) % todas.length;

  return todas[idx];
};

// ── Obtener los 11 jugadores de una formación ─────────────────────────────────
export const obtenerJugadoresFormacion = async (formacionId) => {
  const { data, error } = await supabase
    .from('jugadores')
    .select(`
      id, nombre, apellido, numero_camiseta, slot, nacionalidad,
      posicion:posiciones(sigla),
      club:clubes(sigla)
    `)
    .eq('formacion_id', formacionId)
    .eq('contexto', 'formacion_mitica')
    .order('slot', { ascending: true });

  if (error || !data) return [];
  return data;
};

// ── Guardar resultado en palmares ─────────────────────────────────────────────
export const guardarResultadoFormacion = async (userId, formacionId, completados, total, tiempoRestante) => {
  const gano = completados === total;

  // Incrementar contador en perfiles
  const { data: perfil } = await supabase
    .from('perfiles')
    .select('formaciones_completadas, formaciones_historial')
    .eq('id', userId)
    .single();

  const historialActual = perfil?.formaciones_historial || [];
  const nuevaEntrada = {
    formacion_id: formacionId,
    completados,
    total,
    gano,
    tiempo_restante: tiempoRestante,
    fecha: new Date().toISOString(),
  };

  const updates = {
    formaciones_historial: [...historialActual, nuevaEntrada],
  };

  if (gano) {
    updates.formaciones_completadas = (perfil?.formaciones_completadas || 0) + 1;
  }

  await supabase.from('perfiles').update(updates).eq('id', userId);
  return { gano };
};

// ── Verificar si ya jugó hoy ───────────────────────────────────────────────────
export const checkCooldownFormaciones = async (userId, formacionId) => {
  const { data: perfil } = await supabase
    .from('perfiles')
    .select('formaciones_historial')
    .eq('id', userId)
    .single();

  const historial = perfil?.formaciones_historial || [];
  const hoy = new Date().toDateString();

  const yaJugoHoy = historial.some(h => {
    const fechaH = new Date(h.fecha).toDateString();
    return h.formacion_id === formacionId && fechaH === hoy;
  });

  return yaJugoHoy;
};